import React from 'react';
import {ArcElement, Chart as ChartJS, Legend, Tooltip} from 'chart.js';
import {Doughnut} from 'react-chartjs-2';
import {BrandType} from '@shared/cci/features/const/environment';

ChartJS.register(ArcElement, Tooltip, Legend);

function cssVar(name: string) {
  return getComputedStyle(document.body).getPropertyValue(name);
}

function removeZeroData(data: Array<number>, colors: Array<string>) {
  const filteredData: Array<number> = [];
  const filteredColors: Array<string> = [];

  data.forEach((value, index) => {
    if (value !== 0) {
      filteredData.push(value);
      filteredColors.push(colors[index]);
    }
  });

  return {filteredData, filteredColors};
}

function addGapsToData(data: Array<number>, colors: Array<string>, gapSize: number = 1) {
  const dataWithGaps: Array<number> = [];
  const colorsWithGaps: Array<string> = [];

  if (data.length === 1) {
    dataWithGaps.push(...data);
    colorsWithGaps.push(...colors);
    return {dataWithGaps, colorsWithGaps};
  }

  data.forEach((value, index) => {
    dataWithGaps.push(...[value, gapSize]);
    colorsWithGaps.push(...[colors[index], 'transparent']);
  });

  return {dataWithGaps, colorsWithGaps};
}

const GRAPH_SETTINGS = {
  CUT_OUT: '85%',
  FULL_GRAPH: 100,
  BORDER_WIDTH: 0,
};

interface EnergyPowerRecentGraphProps {
  motorRatio: number;
  electricDeviceRatio: number;
  climateRatio: number;
  batteryManageRatio: number;
}

export default function EnergyPowerRecentGraph({
  motorRatio,
  electricDeviceRatio,
  climateRatio,
  batteryManageRatio,
}: EnergyPowerRecentGraphProps) {
  const brand = import.meta.env.VITE_BRAND;
  const isNoData = motorRatio === 0 && electricDeviceRatio === 0 && climateRatio === 0 && batteryManageRatio === 0;
  const graphData = [motorRatio, electricDeviceRatio, climateRatio, batteryManageRatio];
  const graphColors = [
    // react-chartjs-2 은 ccs 변수 값을 var('-graph-gauge-active') 형식으로 가져오지 못해서
    // cssVar 함수로 브라우저에 있는 스타일 값을 가져오는 방식으로 적용했습니다.
    // 현대만 해당 그래프에서 다른 컬러를 사용하여 .electricity_graph_area 영역에서 색상을 재정의해두었지만,
    // cssVar가 body 영역에서 컬러 값을 가져오기 때문에, 하위에서 재정의한 컬러를 가져오지 못하여 브랜드 분기로 처리해두었습니다.
    cssVar(brand === BrandType.HYUNDAI ? '--di-secondary' : '--graph-gauge-active'),
    cssVar('--blue-custom'),
    cssVar('--di-mint'),
    cssVar('--di-purple'),
  ];
  const {filteredData, filteredColors} = removeZeroData(graphData, graphColors);
  const {dataWithGaps, colorsWithGaps} = addGapsToData(filteredData, filteredColors);

  const chartData = {
    datasets: [
      {
        data: isNoData ? [GRAPH_SETTINGS.FULL_GRAPH] : dataWithGaps,
        backgroundColor: isNoData ? [cssVar('--on-surface-container-mid')] : colorsWithGaps,
        borderWidth: GRAPH_SETTINGS.BORDER_WIDTH,
      },
    ],
  };

  const options = {
    cutout: GRAPH_SETTINGS.CUT_OUT,
    plugins: {
      legend: {display: false},
      tooltip: {enabled: false},
    },
  };

  return (
    <div
      aria-hidden="true"
      tabIndex={-1}
      className="electricty_graph"
      style={{width: '108px', height: '108px'}}
    >
      <Doughnut
        data={chartData}
        options={options}
      />
      <span className="ic ic_filled_charge_normal_18px" />
    </div>
  );
}
